import React, { useEffect, useState } from 'react';
import AlertModal from '../common/AlertModal';
import { axiosInstance, extractApiErrorMessage } from '../../utils/axios';
import PageHeader from './PageHeader';
import './medichat_mypage.scss';

const CardAddModal = ({ open, onClose, onAdded, fincode }) => {
  const [num, setNum] = useState('');
  const [mm, setMM]   = useState('');
  const [yy, setYY]   = useState('');
  const [cvc, setCvc] = useState('');
  const [def, setDef] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(!!window.Fincode);
  const [err, setErr] = useState('');
  const [alertModal, setAlertModal] = useState({ open: false, message: '' });

  const showError = (message) => {
    setErr(message);
    setAlertModal({ open: true, message });
  };

  // 開いたら初期化
  useEffect(() => {
    if (open) {
      setNum(''); setMM(''); setYY(''); setCvc(''); setDef(true);
      setErr('');
    }
  }, [open]);

  // Fincode.js の動的ロード（なければ）
  useEffect(() => {
    if (!open) return;
    if (window.Fincode) { setReady(true); return; }

    const src = (fincode?.js_url && fincode.js_url.trim())
      ? fincode.js_url
      : 'https://js.test.fincode.jp/v1/fincode.js'; // フォールバック

    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => setReady(true);
    el.onerror = () => showError('カード入力画面の読み込みに失敗しました。時間をおいて再度お試しください。');
    document.head.appendChild(el);
    return () => { el.onload = null; el.onerror = null; };
  }, [open, fincode?.js_url]);

  if (!open) return null;

  // 軽いバリデーション
  const cleanNum = (v) => v.replace(/[^\d]/g, '');
  const isValid = () => {
    const n = cleanNum(num);
    const m = Number(mm);
    const y = cleanNum(yy);
    return (
      n.length >= 12 && n.length <= 19 &&
      /^\d{2}$/.test(y) &&
      /^\d{1,2}$/.test(mm) && m >= 1 && m <= 12 &&
      /^\d{3,4}$/.test(cvc) &&
      !!(fincode?.public_key)
    );
  };

  // フロントトークン化 → 保存
  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!ready) { showError('カード入力画面の準備がまだ完了していません。少し待ってから再度お試しください。'); return; }
    if (!isValid()) { showError('カード情報を正しく入力してください。'); return; }
    if (!fincode?.public_key) { showError('カード登録の準備に失敗しました。時間をおいて再度お試しください。'); return; }

    setBusy(true);
    try {
      const fin = window.Fincode(fincode.public_key);
      const expireYYMM = `${String(yy).padStart(2,'0')}${String(mm).padStart(2,'0')}`;

      const token = await new Promise((resolve, reject) => {
        fin.tokens(
          {
            card_no: cleanNum(num),
            expire: expireYYMM, // YYMM
            cvc: cvc,
          },
          (status, res) => {
            if (status === 200 && res?.list?.[0]?.token) {
              resolve(res.list[0].token);
              return;
            }
            const msg = (res?.errors || [])
              .map(e => e?.error_message || e?.message)
              .filter(Boolean).join(' / ') || 'トークン化に失敗しました';
            reject(new Error(msg));
          },
          () => reject(new Error('通信エラーが発生しました'))
        );
      });

      // 取得した token をサーバへ保存
      await axiosInstance.post(
        '/api/medichat/payment_methods',
        {
          card_token: token,
          make_default: def,
          exp_month: Number(mm),
          exp_year: 2000 + Number(yy)
          // cardholder_name を送るUIにしていないため省略
        },
        {
          skipGlobalError: true,
        }
      );

      onAdded?.();
      onClose?.();
    } catch (e2) {
      showError(extractApiErrorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  // 表示用フォーマット
  const fmtNum = (v) => cleanNum(v).replace(/(\d{4})(?=\d)/g, '$1 ').trim();

  return (
    <div className="mypage-modal-backdrop">
      <div className="mypage-modal" role="dialog" aria-modal="true">
        <div className="mypage-modal__header">
          <h3 style={{margin:0}}>カードを追加</h3>
          <button className="mypage-modal__close" onClick={onClose} aria-label="閉じる" disabled={busy}>×</button>
        </div>

        <form className="mypage-modal-body" onSubmit={submit}>
          <label>カード番号
            <input
              value={fmtNum(num)}
              onChange={e=>setNum(e.target.value)}
              inputMode="numeric"
              placeholder="1234 5678 9012 3456"
              disabled={busy}
              required
            />
          </label>

          <div style={{display:'flex',justifyContent:'space-between'}}>
            <label style={{width:'48%'}}>月(MM)
              <input
                value={mm}
                onChange={e=>setMM(e.target.value.replace(/[^\d]/g, '').slice(0,2))}
                inputMode="numeric"
                placeholder="12"
                disabled={busy}
                required
              />
            </label>
            <label style={{width:'48%'}}>年(YY)
              <input
                value={yy}
                onChange={e=>setYY(e.target.value.replace(/[^\d]/g, '').slice(0,2))}
                inputMode="numeric"
                placeholder="28"
                disabled={busy}
                required
              />
            </label>
          </div>
          <label>CVC
              <input
                value={cvc}
                onChange={e=>setCvc(e.target.value.replace(/[^\d]/g, '').slice(0,4))}
                inputMode="numeric"
                placeholder="123"
                disabled={busy}
                required
              />
            </label>

          {/* 初回登録時は自動的に既定になるため、チェックボックスは非表示
          <label style={{display:'flex', gap:8, alignItems:'center', marginTop:8}}>
            <input
              type="checkbox"
              checked={def}
              onChange={e=>setDef(e.target.checked)}
              disabled={busy}
            />
            既定のカードにする
          </label>
          */}

          {err && (
            <div style={{marginTop:8, color:'#c0392b', fontSize:12}}>
              {err}
            </div>
          )}

          <div className="mypage-modal-actions">
            <button type="submit" className="mypage-btn-primary" disabled={busy || !ready} style={{width: '100%'}}>
              {busy ? '登録中…' : '登録'}
            </button>
          </div>
        </form>
      </div>

      <AlertModal
        open={alertModal.open}
        title="カードを登録できませんでした"
        message={alertModal.message}
        iconType="error"
        onClose={() => setAlertModal({ open: false, message: '' })}
      />
    </div>
  );
};

const CARD_BRAND_COLORS = {
  visa:       'linear-gradient(135deg,#1a1f71,#3b5bdb)',
  mastercard: 'linear-gradient(135deg,#eb001b,#f79e1b)',
  amex:       'linear-gradient(135deg,#007bc1,#00b4d8)',
  jcb:        'linear-gradient(135deg,#003087,#009f6b)',
};
const cardGrad = (brand) => CARD_BRAND_COLORS[brand?.toLowerCase()] || 'linear-gradient(135deg,#334155,#475569)';

const actionBtnBase2 = {
  display: 'block', width: '100%', padding: '16px',
  background: 'none', border: 'none', fontSize: '17px',
  textAlign: 'center', cursor: 'pointer', color: '#007AFF',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const PaymentMethodsPage = () => {
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [fincode, setFincode] = useState({ public_key: '', tokenize_url: '' });
  const [actionTarget, setActionTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pressedId, setPressedId] = useState(null);

  const load = async () => {
    const { data } = await axiosInstance.get('/api/medichat/payment_methods');
    setMethods(data?.methods || []);
    setFincode(data?.fincode || {});
  };
  useEffect(() => { load(); }, []);

  const setDefault = async (id) => {
    setBusy(true);
    try { await axiosInstance.post(`/api/medichat/payment_methods/${id}/set_default`); await load(); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await axiosInstance.delete(`/api/medichat/payment_methods/${deleteTarget.id}`); setDeleteTarget(null); await load(); }
    finally { setDeleting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f2f2f7', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* ヘッダー */}
      <PageHeader 
        title="支払い方法" 
        right={
          <button 
            onClick={() => setAddOpen(true)} 
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: '14px', 
              color: '#6366f1', 
              cursor: 'pointer', 
              padding: '8px 0', 
              fontFamily: 'inherit',
              fontWeight: '600'
            }}
          >
            追加
          </button>
        }
      />

      <div style={{ padding: '20px 16px 40px', paddingTop: '70px' }}>
        {/* インフォバー */}
        <div style={{ background: '#e8f4fd', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#0369a1', lineHeight: '1.5' }}>
          クレジットカード/銀行振込/PayPay支払い/Apple・Google Payに対応しています
        </div>

        {!methods ? (
          <div style={{ textAlign: 'center', color: '#8e8e93', padding: '60px 0', fontSize: '15px' }}>読み込み中…</div>
        ) : methods.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: '60px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>💳</div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>カードが未登録です</div>
            <div style={{ fontSize: '15px', color: '#8e8e93', marginBottom: '28px' }}>クレジットカードを登録してください</div>
            <button onClick={() => setAddOpen(true)} style={{ background: '#007AFF', color: '#fff', border: 'none', borderRadius: '14px', padding: '14px 32px', fontSize: '17px', fontWeight: '600', cursor: 'pointer' }}>カードを追加</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {methods.map(m => (
              <div
                key={m.id}
                onMouseDown={() => setPressedId(m.id)}
                onMouseUp={() => setPressedId(null)}
                onMouseLeave={() => setPressedId(null)}
                onTouchStart={() => setPressedId(m.id)}
                onTouchEnd={() => setPressedId(null)}
                onClick={() => setActionTarget(m)}
                style={{
                  borderRadius: '16px', overflow: 'hidden', cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  transform: pressedId === m.id ? 'scale(0.98)' : 'scale(1)',
                  transition: 'transform 0.1s',
                }}
              >
                {/* カード上部: グラデーション */}
                <div style={{
                  background: cardGrad(m.brand),
                  padding: '18px 20px 14px',
                  position: 'relative',
                }}>
                  {m.is_default && (
                    <div style={{
                      position: 'absolute', top: '12px', right: '14px',
                      background: 'rgba(255,255,255,0.25)',
                      borderRadius: '20px', padding: '3px 10px',
                      fontSize: '11px', fontWeight: '600', color: '#fff',
                    }}>デフォルト</div>
                  )}
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', fontWeight: '500' }}>
                    {m.brand?.toUpperCase() || 'CARD'}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#fff', letterSpacing: '2px', fontFamily: 'monospace' }}>
                    •••• •••• •••• {m.last4}
                  </div>
                </div>
                {/* カード下部: 有効期限 */}
                <div style={{ background: '#fff', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '10px', color: '#8e8e93', marginBottom: '2px' }}>有効期限</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#000', fontFamily: 'monospace' }}>
                      {String(m.exp_month).padStart(2,'0')}/{String(m.exp_year).slice(-2)}
                    </div>
                  </div>
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1l6 6-6 6" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* アクションシート */}
      {actionTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          onClick={() => setActionTarget(null)}>
          <div style={{ padding: '8px', paddingBottom: 'max(8px,env(safe-area-inset-bottom))' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', overflow: 'hidden', marginBottom: '8px', backdropFilter: 'blur(40px)' }}>
              <div style={{ padding: '14px 16px 10px', textAlign: 'center', borderBottom: '0.5px solid rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#000' }}>
                  {actionTarget.brand?.toUpperCase() || 'CARD'} •••• {actionTarget.last4}
                </div>
              </div>
              {!actionTarget.is_default && (
                <>
                  <button onClick={() => { setDefault(actionTarget.id); setActionTarget(null); }} disabled={busy} style={actionBtnBase2}>デフォルトに設定</button>
                  <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.1)' }} />
                </>
              )}
              <button onClick={() => { setDeleteTarget(actionTarget); setActionTarget(null); }} style={{ ...actionBtnBase2, color: '#ff3b30' }}>削除</button>
            </div>
            <button onClick={() => setActionTarget(null)} style={{ ...actionBtnBase2, background: 'rgba(255,255,255,0.97)', borderRadius: '14px', fontWeight: '600', backdropFilter: 'blur(40px)' }}>キャンセル</button>
          </div>
        </div>
      )}

      {/* 削除確認 */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', overflow: 'hidden', width: '100%', maxWidth: '280px', textAlign: 'center' }}>
            <div style={{ padding: '20px 16px 0' }}>
              <div style={{ fontSize: '17px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>このカードを削除</div>
              <div style={{ fontSize: '13px', color: '#8e8e93', marginBottom: '16px' }}>
                {deleteTarget.brand?.toUpperCase()} •••• {deleteTarget.last4}
              </div>
            </div>
            <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.15)', display: 'flex' }}>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', borderRight: '0.5px solid rgba(0,0,0,0.15)', fontSize: '17px', color: '#007AFF', cursor: 'pointer' }}>キャンセル</button>
              <button onClick={remove} disabled={deleting} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', fontSize: '17px', fontWeight: '600', color: '#ff3b30', cursor: 'pointer' }}>{deleting ? '削除中…' : '削除'}</button>
            </div>
          </div>
        </div>
      )}

      <CardAddModal open={addOpen} onClose={() => setAddOpen(false)} onAdded={load} fincode={fincode} />
    </div>
  );
};

export default PaymentMethodsPage;
